import { toast } from "react-toastify";
import {
    assetSocketDisconnected,
    socketIsLive,
    socketIsDead,
    assetCombineData,
} from '../../features/assets/assetsSlice';
import { checkSockEvent } from '../../features/events/eventsApi';
import { socketTrackBuilt } from '../../features/datacenter/historySlice';
import { socketReportBuilt } from '../../features/datacenter/reportSlice';
import { getPdf } from '../../features/datacenter/datacenterApi';
import {
    updateSocketZone, updateSocketMarker,
    deleteSocketZone, deleteSocketMarker,
} from '../../features/places/placesSlice';

const PROTOCOL_VERSION = 1;
const NATIVE_SOCKET_URL_OVERRIDE = process.env.REACT_APP_NATIVE_SOCKET_URL || '';
const EVENT_HOLD_TIME_MS = 3000;
const FOCUS_DEBOUNCE_MS = 200;
const MAX_CLIENT_BATCH_SIZE = 100;
const CHUNK_SPACING_MS = 75;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 5;

const legacyActivityTypes = {
    combineData: 'telemetry.update',
    eventData: 'event.created',
    reportBuilt: 'report.built',
    pdfReportReady: 'report.pdf_ready',
    pdfReportEmailSent: 'report.email_sent',
    errMessage: 'error.message',
    zone_updated: 'place.zone_updated',
    marker_updated: 'place.marker_updated',
    zone_deleted: 'place.zone_deleted',
    marker_deleted: 'place.marker_deleted',
};

function normalizeNativeSocketUrl(host) {
    if (NATIVE_SOCKET_URL_OVERRIDE) {
        return NATIVE_SOCKET_URL_OVERRIDE;
    }

    if (!host) return '';

    if (host.endsWith('/ws')) {
        return host;
    }

    try {
        const parsed = new URL(host);
        const protocol = parsed.protocol === 'ws:' ? 'ws:' : 'wss:';
        return `${protocol}//${parsed.hostname}:10008/ws`;
    } catch {
        return `wss://${host}:10008/ws`;
    }
}

function normalizeMessage(rawMessage) {
    if (!rawMessage || typeof rawMessage !== 'object') return null;

    if (rawMessage.type) {
        return {
            version: rawMessage.v,
            type: rawMessage.type,
            payload: rawMessage.payload,
            seq: rawMessage.seq,
            ts: rawMessage.ts,
        };
    }

    if (rawMessage.activity) {
        return {
            version: PROTOCOL_VERSION,
            type: legacyActivityTypes[rawMessage.activity],
            payload: rawMessage.payload,
            legacyActivity: rawMessage.activity,
        };
    }

    return null;
}

function errorText(payload) {
    if (!payload) return 'LiveData error';
    if (typeof payload === 'string') return payload;
    return payload.errMessage || payload.message || payload.error || 'LiveData error';
}

const nativeWebSocketMiddleware = () => {
    let socket = null;
    let status = 'idle';
    let manualDisconnect = false;
    let reconnectAttempt = 0;
    let reconnectTimer = null;
    let authToken = null;
    let nativeUrl = null;
    let focusedImei = null;
    let focusDebounceTimer = null;
    let eventDataTime = null;
    let sockEventBuffer = [];
    let batchChunkTimers = [];
    let readyNotified = false;
    let lastConnectPayload = null;

    const clearReconnectTimer = () => {
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
        }
    };

    const clearFocusTimer = () => {
        if (focusDebounceTimer) {
            clearTimeout(focusDebounceTimer);
            focusDebounceTimer = null;
        }
    };

    const clearBatchTimers = () => {
        batchChunkTimers.forEach(timer => clearTimeout(timer));
        batchChunkTimers = [];
    };

    const clearEventBuffer = () => {
        eventDataTime = null;
        sockEventBuffer = [];
    };

    const sendMessage = (type, payload = {}) => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return false;
        socket.send(JSON.stringify({ v: PROTOCOL_VERSION, type, payload }));
        return true;
    };

    const closeSocket = () => {
        if (!socket) return;
        const closingSocket = socket;
        socket = null;
        closingSocket.onopen = null;
        closingSocket.onmessage = null;
        closingSocket.onerror = null;
        closingSocket.onclose = null;
        try {
            closingSocket.close();
        } catch {
            // Best effort cleanup only.
        }
    };

    const getFocusedImei = store => {
        const state = store.getState();
        const detailView = state?.assets?.detailView;
        if (!detailView) return null;

        const normalized = `${detailView}`;
        const assetList = state?.assets?.assetList || [];
        return assetList.map(assetId => `${assetId}`).includes(normalized) ? normalized : null;
    };

    const sendFocusedImei = () => {
        if (status !== 'live') return false;
        return sendMessage('interest.update', { focusedImei });
    };

    const queueFocusedImei = (store, immediate = false) => {
        const nextFocusedImei = getFocusedImei(store);
        if (nextFocusedImei === focusedImei && !immediate) return;

        focusedImei = nextFocusedImei;
        clearFocusTimer();

        if (immediate) {
            sendFocusedImei();
            return;
        }

        focusDebounceTimer = setTimeout(() => {
            focusDebounceTimer = null;
            sendFocusedImei();
        }, FOCUS_DEBOUNCE_MS);
    };

    const dispatchEvent = (store, payload) => {
        const now = Date.now();

        if (!eventDataTime) {
            store.dispatch(checkSockEvent([payload]));
            eventDataTime = now;
            return;
        }

        if (now - eventDataTime > EVENT_HOLD_TIME_MS) {
            const bufferedEvents = sockEventBuffer.length > 0 ? [...sockEventBuffer, payload] : [payload];
            store.dispatch(checkSockEvent(bufferedEvents));
            sockEventBuffer = [];
            eventDataTime = now;
            return;
        }

        sockEventBuffer.push(payload);
    };

    const dispatchTelemetry = (store, type, payload) => {
        if (type !== 'telemetry.batch' || !Array.isArray(payload) || payload.length <= MAX_CLIENT_BATCH_SIZE) {
            store.dispatch(assetCombineData(payload));
            return;
        }

        clearBatchTimers();
        for (let start = 0; start < payload.length; start += MAX_CLIENT_BATCH_SIZE) {
            const chunk = payload.slice(start, start + MAX_CLIENT_BATCH_SIZE);
            const timer = setTimeout(() => {
                store.dispatch(assetCombineData(chunk));
            }, batchChunkTimers.length * CHUNK_SPACING_MS);
            batchChunkTimers.push(timer);
        }
    };

    const dispatchReportBuilt = (store, payload) => {
        if (payload?.success) {
            toast.success(`${payload.reportName} Report ready for ${payload.assetName}`);
            if (payload.reportName === 'Track') {
                store.dispatch(socketTrackBuilt(payload));
            } else {
                store.dispatch(socketReportBuilt(payload));
            }
            return;
        }

        toast.error(`${payload?.assetName || 'Asset'} Report could not be built. Please try again`);
    };

    const dispatchMessage = (store, message) => {
        switch (message.type) {
            case 'connection.ready':
                status = 'live';
                reconnectAttempt = 0;
                readyNotified = true;
                store.dispatch(socketIsLive());
                toast.success("LiveData Connected");
                queueFocusedImei(store, true);
                break;

            case 'connection.error':
                status = 'closed';
                readyNotified = false;
                store.dispatch(assetSocketDisconnected());
                store.dispatch(socketIsDead());
                toast.error(errorText(message.payload));
                closeSocket();
                if (!manualDisconnect) {
                    scheduleReconnect(store);
                }
                break;

            case 'telemetry.snapshot':
            case 'telemetry.update':
            case 'telemetry.batch':
                dispatchTelemetry(store, message.type, message.payload);
                break;

            case 'event.created':
                dispatchEvent(store, message.payload);
                break;

            case 'report.built':
                dispatchReportBuilt(store, message.payload);
                break;

            case 'report.pdf_ready': {
                const reportId = message.payload?.reportSummary?.reportID;
                if (reportId) {
                    store.dispatch(getPdf(reportId));
                }
                break;
            }

            case 'report.email_sent':
                toast.success(`Report emails have been sent`);
                break;

            case 'error.message':
                toast.error(errorText(message.payload));
                break;

            case 'place.zone_updated':
                toast.success("Zone updated");
                store.dispatch(updateSocketZone(message.payload?.data_load));
                break;

            case 'place.marker_updated':
                toast.success("Marker updated");
                store.dispatch(updateSocketMarker(message.payload?.data_load));
                break;

            case 'place.zone_deleted':
                toast.error("Zone deleted");
                store.dispatch(deleteSocketZone(message.payload));
                break;

            case 'place.marker_deleted':
                toast.error("Marker deleted");
                store.dispatch(deleteSocketMarker(message.payload));
                break;

            case 'ping':
                sendMessage('pong', message.payload || {});
                break;

            case 'pong':
                break;

            default:
                if (message.type) {
                    console.warn('Unknown native WebSocket message type:', message.type, message);
                }
                break;
        }
    };

    function scheduleReconnect(store) {
        clearReconnectTimer();

        if (manualDisconnect || !lastConnectPayload?.host || !authToken) return;

        if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
            toast.error('LiveData reconnect failed');
            return;
        }

        reconnectAttempt += 1;
        const backoffDelay = Math.min(
            BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempt - 1),
            MAX_RECONNECT_DELAY_MS
        );
        const jitter = Math.floor(Math.random() * 500);

        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connect(store, lastConnectPayload);
        }, backoffDelay + jitter);
    }

    const handleClose = (store, activeSocket) => () => {
        if (socket !== activeSocket) return;

        socket = null;
        readyNotified = false;
        status = 'closed';
        store.dispatch(socketIsDead());

        if (manualDisconnect) {
            return;
        }

        toast.error(`LiveData Disconnected`);
        scheduleReconnect(store);
    };

    const handleError = store => error => {
        console.error('Native WebSocket Error:', error);
        store.dispatch(assetSocketDisconnected());
        if (!readyNotified) {
            toast.error(`LiveData Connection error`);
        }
    };

    const handleMessage = store => event => {
        if (typeof event.data !== 'string') {
            console.warn('Ignoring non-text native WebSocket message');
            return;
        }

        let parsed;
        try {
            parsed = JSON.parse(event.data);
        } catch {
            console.warn('Ignoring malformed native WebSocket message:', event.data);
            return;
        }

        const message = normalizeMessage(parsed);
        if (!message?.type) {
            console.warn('Ignoring unsupported native WebSocket message:', parsed);
            return;
        }

        if (message.version !== PROTOCOL_VERSION) {
            console.warn('Ignoring unsupported native WebSocket protocol version:', message.version);
            return;
        }

        dispatchMessage(store, message);
    };

    function connect(store, payload) {
        if (!payload?.host || !payload?.token) {
            console.warn('No host or token provided for native WebSocket connection');
            return;
        }

        clearReconnectTimer();
        clearBatchTimers();
        closeSocket();

        authToken = payload.token;
        lastConnectPayload = payload;
        nativeUrl = normalizeNativeSocketUrl(payload.host);
        manualDisconnect = false;
        readyNotified = false;
        status = 'connecting';

        try {
            socket = new WebSocket(nativeUrl);
            const activeSocket = socket;

            activeSocket.onopen = () => {
                if (socket !== activeSocket) return;
                status = 'authenticating';
                sendMessage('auth', { token: authToken });
            };

            activeSocket.onmessage = handleMessage(store);
            activeSocket.onerror = handleError(store);
            activeSocket.onclose = handleClose(store, activeSocket);
        } catch (error) {
            console.error('Error creating native WebSocket connection:', error);
            store.dispatch(assetSocketDisconnected());
            scheduleReconnect(store);
        }
    }

    const disconnect = store => {
        manualDisconnect = true;
        status = 'closing';
        clearReconnectTimer();
        clearFocusTimer();
        clearBatchTimers();
        clearEventBuffer();
        focusedImei = null;
        closeSocket();
        status = 'closed';
        readyNotified = false;
        store.dispatch(socketIsDead());
    };

    return store => next => action => {
        switch (action.type) {
            case 'assetSocketConnect':
                connect(store, action.payload);
                return;

            case 'assetSocketDisconnect':
                disconnect(store);
                return;

            default: {
                const result = next(action);
                queueFocusedImei(store);
                return result;
            }
        }
    };
};

export default nativeWebSocketMiddleware;
